import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
} from "@mui/material";
import { LocalUser } from "../../../utils/UploadFile";
import { fetchDocuments } from "../../../utils/FetchDocuments";
import { uploadFile } from "../../../utils/UploadFile";
import { UploadedDocument } from "../../../utils/FetchDocuments";
import { useParams, useNavigate } from "react-router-dom";
import { useFirebaseAuth } from "../../../hooks/useFirebaseAuth";
import FileUploadSection, {
  UploadedFile,
} from "../../../components/FileUploadSection";

export const fetchAllDocuments = async ({
  tripId,
  setUploadedIncomeDocuments,
  setUploadedExpenseDocuments,
}: {
  tripId: string;
  setUploadedIncomeDocuments: (documents: UploadedFile[]) => void;
  setUploadedExpenseDocuments: (documents: UploadedFile[]) => void;
}) => {
  try {
    const incomeDocsPromise = fetchDocuments({
      documentContext: "trip",
      documentTypeId: tripId || "",
      documentType: "trip-income",
    });

    const expenseDocsPromise = fetchDocuments({
      documentContext: "trip",
      documentTypeId: tripId || "",
      documentType: "expense",
    });

    const [incomeDocs, expenseDocs] = await Promise.all([
      incomeDocsPromise,
      expenseDocsPromise,
    ]);

    // Convert UploadedDocument to UploadedFile format
    setUploadedIncomeDocuments(
      incomeDocs.map((doc) => ({
        id: doc.id,
        fileName: doc.fileName,
        presignedUrl: doc.presignedUrl,
      }))
    );
    setUploadedExpenseDocuments(
      expenseDocs.map((doc) => ({
        id: doc.id,
        fileName: doc.fileName,
        presignedUrl: doc.presignedUrl,
      }))
    );
  } catch (error) {
    console.error("Error fetching documents:", error);
  }
};

const AddProof = () => {
  const { tripId } = useParams();
  const { user, loading: authLoading } = useFirebaseAuth();
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : null;
  const navigate = useNavigate();
  const [selectedIncomeFiles, setSelectedIncomeFiles] = useState<File[]>([]);
  const [selectedExpenseFiles, setSelectedExpenseFiles] = useState<File[]>([]);
  const [uploadedIncomeDocuments, setUploadedIncomeDocuments] = useState<
    UploadedFile[]
  >([]);
  const [uploadedExpenseDocuments, setUploadedExpenseDocuments] = useState<
    UploadedFile[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user && tripId && !authLoading) {
      fetchAllDocuments({
        tripId,
        setUploadedIncomeDocuments,
        setUploadedExpenseDocuments,
      });
    }
  }, [tripId, user, authLoading]);

  const handleUpload = async (type: "income" | "expense") => {
    const filesToUpload =
      type === "income" ? selectedIncomeFiles : selectedExpenseFiles;
    if (filesToUpload.length === 0) return;

    setIsLoading(true);
    try {
      const uploadPromises = filesToUpload.map((file) =>
        uploadFile({
          file,
          documentType: type === "income" ? "trip-income" : "expense",
          documentTypeId: tripId || "",
          documentContext: "trip",
          description: `Trip ${type} proof`,
          companyId: companyId,
        })
      );

      await Promise.all(uploadPromises);
      await fetchAllDocuments({
        tripId: tripId || "",
        setUploadedIncomeDocuments,
        setUploadedExpenseDocuments,
      });

      // Clear selected files after successful upload
      if (type === "income") {
        setSelectedIncomeFiles([]);
      } else {
        setSelectedExpenseFiles([]);
      }
    } catch (error) {
      console.error("Error uploading files:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreview = (url: string) => {
    window.open(url, "_blank");
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Income Proofs Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <FileUploadSection
          title="Income Proofs"
          selectedFiles={selectedIncomeFiles}
          uploadedFiles={uploadedIncomeDocuments}
          onFileSelect={(files) =>
            setSelectedIncomeFiles((prev) => [...prev, ...files])
          }
          onFileRemove={(index) =>
            setSelectedIncomeFiles((prev) => prev.filter((_, i) => i !== index))
          }
          onUpload={() => handleUpload("income")}
          onPreview={handlePreview}
          isLoading={isLoading}
          buttonText="Select Income Proofs"
          uploadButtonText="Upload Selected Income Proofs"
        />
      </Paper>

      {/* Expense Proofs Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <FileUploadSection
          title="Expense Proofs"
          selectedFiles={selectedExpenseFiles}
          uploadedFiles={uploadedExpenseDocuments}
          onFileSelect={(files) =>
            setSelectedExpenseFiles((prev) => [...prev, ...files])
          }
          onFileRemove={(index) =>
            setSelectedExpenseFiles((prev) =>
              prev.filter((_, i) => i !== index)
            )
          }
          onUpload={() => handleUpload("expense")}
          onPreview={handlePreview}
          isLoading={isLoading}
          buttonText="Select Expense Proofs"
          uploadButtonText="Upload Selected Expense Proofs"
        />
      </Paper>

      <Box
        display="flex"
        justifyContent="flex-end"
        gap={2}
        sx={{ mt: 3, mb: 3 }}
      >
        <Button
          variant="outlined"
          color="primary"
          onClick={() => navigate(`/trips`)}
        >
          Skip
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(`/trips`)}
        >
          Done
        </Button>
      </Box>
    </Container>
  );
};

export default AddProof;
